import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import {
  isTokenValid,
  SessionService,
  UserDataService
} from '@com-delta-idp/mnx-oauth';
import { TrackerFacade } from '@com-delta-idp/mnx-skymiles-overview';
import {
  AspectType,
  ErrorMessage,
  Tracker,
  TrackerCollapsedForStandardMocks
} from '@com-delta-omnichannel/aura';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'idp-flyout-tracker',
  templateUrl: './flyout-tracker.component.html',
  styles: ['']
})
export class FlyoutTrackerComponent implements OnInit, OnDestroy {
  content: Tracker = TrackerCollapsedForStandardMocks;
  errorMessage: ErrorMessage = {} as ErrorMessage;
  trackerFacadeData = new BehaviorSubject<Tracker>({} as Tracker);
  trackerFacadeError = new BehaviorSubject<ErrorMessage>({} as ErrorMessage);
  subTitleLink = new BehaviorSubject<boolean>(false);
  private unsubscribe: Subject<void> = new Subject();

  @Input() flyoutContent;
  @Input() resAcctSumDetails;

  spinner = false;
  hrefSet = false;
  memberShipLevel: any;
  medallionUrl: any;

  constructor(
    public trackerFacade: TrackerFacade,
    private userData: UserDataService,
    private _el: ElementRef,
    private session: SessionService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  @HostListener('click', ['$event']) onClick(event) {
    if (isPlatformBrowser(this.platformId)) {
      if (event.target.matches(['img'])) {
        this.increaseToolTip_zIndex();
      }
    }
  }

  @HostListener('window:keydown.enter', ['$event'])
  handleKeyDown(event) {
    if (isPlatformBrowser(this.platformId)) {
      if (event.target.matches(['button'])) {
        this.increaseToolTip_zIndex();
      }
    }
  }

  ngOnInit(): void {
    this.validateToken();
  }

  async validateToken() {
    if (!this.session.token) {
      await this.session.validateUserSession();
      this.init();
    } else if (!isTokenValid(this.session.token)) {
      await this.session.validateUserSession();
      this.init();
    } else {
      this.init();
    }
  }

  init() {
    if (isPlatformBrowser(this.platformId)) {
      this.userData.getAuthConfigContent().subscribe((response) => {
        if (response && response.ORION_FLYOUT === true) {
          this.getAuraTrackerData();
        }
      });
    }
  }

  getAuraTrackerData() {
    this.trackerFacade.init();
    this.trackerFacade.tracker$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        if (!res.loaded) {
          this.spinner = true;
        }
        if (res.error) {
          const { error } = res;
          const contentErrorMessage: ErrorMessage = {
            ...error,
            aspect: 'collapse' as AspectType,
            action: {
              label: 'Learn How To Qualify For Status',
              url: this.flyoutContent.waiver[0].waiverUrl
            }
          };
          this.trackerFacadeError.next(contentErrorMessage);
          this.spinner = false;
        } else if (res.earnometer) {
          this.trackerFacadeError.next({} as ErrorMessage);
          const trackerContentDeepCopy = JSON.parse(JSON.stringify(res));
          this.setTierLevelUrl(trackerContentDeepCopy);
          const newContent = this.transformAuraContent(trackerContentDeepCopy);
          this.trackerFacadeData.next(newContent);
          this.spinner = false;
        }
      });
  }

  getDefaultErrorMessage(): ErrorMessage {
    const contentErrorMessage: ErrorMessage = {
      aspect: 'collapse' as AspectType,
      message: {
        title:
          'Sorry, we’re currently unable to display your SkyMiles Tracker. Please try again later.',
        subtitle:
          'Sorry, we’re currently unable to display your SkyMiles Tracker. Please try again later.'
      },
      title: '',
      type: 'single-error',
      disable: false,
      action: {
        label: 'Learn How To Qualify For Status',
        url: this.flyoutContent.waiver[0].waiverUrl
      }
    };
    return contentErrorMessage;
  }

  getTierName(name): string {
    const tier = {
      'Gold Medallion': 'GM',
      'Silver Medallion': 'FO',
      'Diamond Medallion': 'DM',
      'Platinum Medallion': 'PM',
      'SkyMiles Member': 'FF'
    };

    return tier[name];
  }

  transformAuraContent(content: Tracker) {
    content.header.aspect = 'collapse';
    content.aspect = 'collapse';
    content.legends.types = ['earned', 'pending'];
    content.header.action = {
      label: content.header.subtitle,
      url: this.medallionUrl
    };

    let sentence = content.header.title.toLowerCase();
    const words = sentence.split(' ');
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );
    const capitalizedSentence = capitalizedWords.join(' ');
    content.header.title = capitalizedSentence;
    return content;
  }

  transformAuraHeaderSubtitle() {
    const element = document.querySelector(
      'idp-flyout-tracker .aura-tracker__collapse .aura-tracker__header__collapse .aura-tracker-header__collapse .aura-tracker-header__collapse__subtitle'
    ) as HTMLElement;
    if (element) {
      element.innerHTML = `<a href=${this.medallionUrl}>${element.textContent}</a>`;
      this.hrefSet = true;
    }
  }

  setTierLevelUrl(res: Tracker) {
    if (this.flyoutContent?.medallion?.length > 0) {
      this.flyoutContent?.medallion.find((tierLevel) => {
        if (tierLevel.medallionId === this.getTierName(res.header.subtitle)) {
          this.medallionUrl = tierLevel.medallionUrl;
        }
      });
    }
  }

  redirect(url: string) {
    if (url.includes('how-to-qualify')) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  }

  increaseToolTip_zIndex() {
    const element = document.querySelector(
      '.cdk-overlay-container'
    ) as HTMLElement;
    if (element) {
      element.style.zIndex = '1060';
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribe.unsubscribe();
  }
}
